import { IonListHeader } from '@ionic/react';
import { useFormikContext } from 'formik';
import { DebouncedFunc, debounce, get } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDeepCompareEffect } from 'react-use';
import * as yup from 'yup';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { FlowItemEditCommonProps } from '../../../../shared/component/flow-item/edit/flow-item-edit.types';
import { ParametersList } from '../../../../shared/component/flow-item-parameter/parameter-list';
import { WidgetExtensions } from '../../../../shared/component/widget/widget.types';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  FlowItemForm,
  FlowItemParameterMetadata,
} from '../../../../shared/model/flow.types';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { getCuttingParameterMetadataFromForm } from '../../util/beacon-cutting.util';

export interface CutProcessSettingsFormProps
  extends Omit<FlowItemEditCommonProps, 'item'> {
  extensions?: WidgetExtensions;
  readOnly?: boolean;
  parameterOptions?: BeaconCuttingData.GetParameterMetadataFromFormOptions;
  setValidationSchema: (schema: yup.AnySchema | undefined) => void;
  formikValuePath?: string;
  parameterListPrefix?: string;
  recalculateParameterValues?: (form: FlowItemForm) => FlowItemForm;
}

const CutProcessSettingsFormHeader = (props: {
  header?: string;
}): ReactElement => {
  const { header } = props;

  return (
    <div className="configuration-header">
      <IonListHeader>
        {header ??
          getLocalizedText('common:application_settings_parameters_header')}
      </IonListHeader>
    </div>
  );
};

const CutProcessSettingsFormWithHeader = (
  props: CutProcessSettingsFormProps & { header?: string }
): ReactElement => {
  const { header, ...cutProcessSettingsProps } = props;

  return (
    <>
      <CutProcessSettingsFormHeader header={header} />
      <CutProcessSettingsForm {...cutProcessSettingsProps} />
    </>
  );
};

const CutProcessSettingsForm = (
  props: CutProcessSettingsFormProps
): ReactElement | null => {
  const {
    extensions,
    readOnly,
    system,
    setValidationSchema,
    formikValuePath,
    parameterListPrefix,
    parameterOptions,
    recalculateParameterValues,
  } = props;

  const { flow, flowItemService } = useFlowServices();
  const [parameters, setParameters] = useState<FlowItemParameterMetadata[]>([]);

  const formik = useFormikContext<FlowItemForm>();

  const [parameterFormOptions, setParameterFormOptions] =
    useState<BeaconCuttingData.GetParameterMetadataFromFormOptions>(
      parameterOptions ?? {}
    );

  useDeepCompareEffect(() => {
    setParameterFormOptions({
      ...parameterOptions,
    });
  }, [parameterOptions ?? {}]);

  const onChangeForm = useCallback(
    (
      form: FlowItemForm,
      parameterFormOptions?: BeaconCuttingData.GetParameterMetadataFromFormOptions
    ) => {
      let actualForm: FlowItemForm = formikValuePath
        ? get(form, formikValuePath)
        : form;

      if (recalculateParameterValues) {
        actualForm = recalculateParameterValues(actualForm);
      }

      const params = getCuttingParameterMetadataFromForm(
        flowItemService,
        actualForm.parameters,
        parameterFormOptions
      );

      const schema = flowItemService.getYupSchemaForItem(
        BeaconCuttingData.FlowItemRef.CUT,
        props.system,
        { flow, form: actualForm },
        params
      );

      setParameters(params);
      setValidationSchema(schema);
    },
    [
      formikValuePath,
      flowItemService,
      props.system,
      setValidationSchema,
      recalculateParameterValues,
      flow,
    ]
  );

  const onChangeFormRef =
    useRef<
      DebouncedFunc<
        (
          form: FlowItemForm,
          parameterFormOptions?: BeaconCuttingData.GetParameterMetadataFromFormOptions
        ) => void
      >
    >();

  useEffect(() => {
    if (onChangeForm) {
      // The function is being debounced to prevent multiple change events
      // from being called. This is particularly useful if anything
      // is monitoring form changes (e.g. recommendations).
      onChangeFormRef.current = debounce(onChangeForm);
    }
  }, [onChangeForm]);

  useEffect(() => {
    if (onChangeFormRef.current) {
      onChangeFormRef.current(formik.values, parameterFormOptions);
    }

    return () => {
      if (onChangeFormRef.current) {
        // We want to prevent set state calls from happening
        // if this component is unmounted.
        onChangeFormRef.current.cancel();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(formik.values), parameterFormOptions]);

  const parameterExtensions = useMemo(() => {
    return {
      widgetExtensions: extensions,
    };
  }, [extensions]);

  return (
    <ParametersList
      extensions={parameterExtensions}
      parameters={parameters}
      system={system}
      readOnly={readOnly}
      formPrefix={parameterListPrefix}
    />
  );
};

export {
  CutProcessSettingsForm,
  CutProcessSettingsFormHeader,
  CutProcessSettingsFormWithHeader,
};
